import { Lock, Unlock } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/components/RefClick/AuthProvider";
import SparkleBurst, { useSparkleTrigger } from "@/components/RefClick/SparkleBurst";

/**
 * Header lock chip.
 *
 * States:
 *   - Locked → open-lock icon slot with muted styling; click prompts for
 *     PIN. Tooltip: "Locked — click to unlock (PIN required for changes)".
 *   - Unlocked → closed-lock icon with accent styling; click locks again.
 *     Tooltip: "Unlocked — click to lock".
 *
 * Tap-target is ≥ 36×36 for a comfortable phone press. Icons alone
 * carry the state so the tooltip is a hint, not a requirement.
 */
export default function LockChip() {
  const { unlocked, promptForPin, lock, ready, pinEnabled } = useAuth();
  const sparkle = useSparkleTrigger();

  // Kill-switch: nothing to show if the PIN system is disabled server-side.
  if (!pinEnabled) return null;

  const handleClick = async () => {
    if (unlocked) {
      lock();
    } else {
      try {
        await promptForPin();
      } catch {
        /* user cancelled — no toast, it's a silent no-op */
      }
    }
  };

  const Icon = unlocked ? Unlock : Lock;
  const label = unlocked
    ? "Unlocked · click to lock"
    : "Locked · click to unlock (PIN required for changes)";
  const ariaLabel = unlocked ? "Unlocked. Click to lock." : "Locked. Click to unlock.";

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={handleClick}
            onPointerEnter={sparkle.onEnter}
            onPointerDown={sparkle.onPress}
            disabled={!ready}
            data-testid="lock-chip"
            data-unlocked={unlocked ? "true" : "false"}
            aria-label={ariaLabel}
            className={
              "focus-accent relative inline-flex h-8 w-8 shrink-0 items-center justify-center overflow-visible rounded-md transition-colors " +
              (unlocked
                ? "bg-accent/15 text-accent hover:bg-accent/25"
                : "text-text-muted hover:bg-white/[0.06] hover:text-accent")
            }
          >
            <Icon className="h-4 w-4" />
            <SparkleBurst trigger={sparkle.count} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          sideOffset={6}
          className="border-white/10 bg-black/80 font-mono-safe text-[11px] text-text-main"
        >
          {label}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
